import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '@/components/shared/Footer';

export default function BegleitungPage() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-bone" data-testid="begleitung-page">
      <div className="max-w-5xl mx-auto px-6 pt-8">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground font-body text-sm transition-colors">
          <ArrowLeft size={16} /> <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>
      </div>

      {/* Hero Image */}
      <section className="max-w-5xl mx-auto px-6 pb-4">
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img src="https://static.prod-images.emergentagent.com/jobs/e0acc782-1a22-4dc5-a2d2-0792ba0290c7/images/0f78d87eed4905389feee5efb3f0279c2e60dfc668f6c60a7ada37254a6df3a5.jpeg" alt="Frau auf einem Weg durch die Wiese – wieder bei sich ankommen" className="w-full h-[250px] md:h-[360px] object-cover object-center" />
        </div>
      </section>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-12 pb-8">
        <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">EIN WEG, KEIN PROGRAMM</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-[1.1]">
          Wieder bei mir ankommen
        </h1>
        <p className="font-body text-base text-foreground/50 mt-6 max-w-xl leading-relaxed">
          Manchmal braucht es keine grosse Veränderung. Manchmal braucht es zuerst einen ehrlichen Blick.
        </p>
      </section>

      {/* Fragen */}
      <section className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-2">
          {['Wo stehe ich?', 'Was trägt mich?', 'Was fehlt mir?', 'Was möchte ich noch erleben?'].map((q, i) => (
            <p key={i} className="font-display text-xl md:text-2xl font-medium text-foreground/60 italic">{q}</p>
          ))}
        </div>
        <div className="mt-10 space-y-4 max-w-xl">
          <p className="font-body text-foreground/50 leading-relaxed">
            Meine Begleitung beginnt nicht mit der Frage:<br />
            <span className="text-foreground/40 italic">"Was muss anders werden?"</span>
          </p>
          <p className="font-body text-foreground/60 leading-relaxed">
            Sondern:<br />
            <span className="font-display text-lg font-medium text-terracotta/80 italic">"Was ist bereits in dir vorhanden, das wieder mehr Raum bekommen darf?"</span>
          </p>
        </div>
      </section>

      {/* 4 Schritte */}
      <section className="bg-warmgray/50">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-display text-3xl font-medium tracking-tight mb-12 text-center">Vier Schritte unserer Begegnung</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { num: '1', title: 'Wahrnehmen', items: ['Wo stehst du heute?', 'Was beschäftigt dich?', 'Was funktioniert gut?', 'Wo spürst du Sehnsucht nach Veränderung?'] },
              { num: '2', title: 'Erkennen', items: ['Deine Stärken.', 'Deine Werte.', 'Deine Wünsche.', 'Deine persönlichen Muster.'], note: 'Oft sehen andere Menschen etwas in uns, das wir selbst übersehen.' },
              { num: '3', title: 'Ausrichten', items: ['Was passt heute noch zu dir?', 'Was möchtest du behalten?', 'Was möchtest du loslassen?', 'Was möchtest du wagen?'] },
              { num: '4', title: 'Leben', items: ['Wie wird aus Erkenntnis Alltag?', 'Wie möchtest du dich zeigen?', 'Wie möchtest du deinen Körper, dein Umfeld, deine Beziehungen und deine Zeit gestalten?'] },
            ].map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border/60">
                <span className="font-display text-4xl font-bold text-terracotta/15">{step.num}</span>
                <h3 className="font-display text-2xl font-medium mt-2 mb-4">{step.title}</h3>
                <div className="space-y-2">
                  {step.items.map((item, j) => (
                    <p key={j} className="font-body text-sm text-foreground/60">{item}</p>
                  ))}
                </div>
                {step.note && <p className="font-body text-xs text-foreground/40 italic mt-4">{step.note}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="font-display text-3xl font-medium tracking-tight mb-4">Vielleicht ist jetzt deine Zeit.</h2>
        <p className="font-body text-foreground/50 mb-8 max-w-md mx-auto">
          Nicht, um jemand anderes zu werden. Sondern um dich selbst wieder bewusster wahrzunehmen.
        </p>
        <Button onClick={() => window.location.href = 'mailto:info@belosophie.com?subject=Erstes Gespräch'} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-10 py-6 text-lg font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all">
          Vereinbare ein erstes Gespräch <ArrowRight size={20} className="ml-2" />
        </Button>
        <p className="font-body text-xs text-foreground/30 mt-4">info@belosophie.com · Individuell. Persönlich. Auf Augenhöhe.</p>
      </section>

      <Footer />
    </div>
  );
}
